import { useMemo } from 'react'
import type { ConversationTurnEntry, Command } from '@/index'
import { EventType } from '@/index'
import {
  isDuplicateUserPromptEvent,
  shouldRenderInTimeline,
} from '@/features/events/conversation/state/types'

export enum TimelineItemType {
  AGENT_EVENT,
  USER_COMMAND,
}

export enum Actor {
  AGENT,
  USER,
}

export type TimelineItem =
  | { type: TimelineItemType.AGENT_EVENT; data: ConversationTurnEntry }
  | { type: TimelineItemType.USER_COMMAND; data: Command }

export interface MessageGroup {
  readonly actor: Actor
  readonly items: TimelineItem[]
}

interface UseConversationTimelineReturn {
  readonly messageGroups: MessageGroup[]
}

export function useConversationTimeline(
  events: ConversationTurnEntry[],
  userCommands: Command[],
): UseConversationTimelineReturn {
  const messageGroups = useMemo(() => {
    const timelineItems = mergeTimelineItems(events, userCommands)
    return groupItemsByActor(timelineItems)
  }, [events, userCommands])

  return { messageGroups }
}

function mergeTimelineItems(
  events: ConversationTurnEntry[],
  userCommands: Command[],
): TimelineItem[] {
  const filteredEvents = events.filter((event) => !isDuplicateUserPromptEvent(event, userCommands))
  const eventItems: TimelineItem[] = filteredEvents.map((event) => ({
    type: TimelineItemType.AGENT_EVENT,
    data: event,
  }))
  const commandItems: TimelineItem[] = userCommands
    .filter(shouldRenderInTimeline)
    .map((cmd) => ({ type: TimelineItemType.USER_COMMAND, data: cmd }))

  const allItems = [...eventItems, ...commandItems]

  const itemsWithParsedTime = allItems.map((item) => ({
    item,
    timestamp: new Date(item.data.timestamp).getTime(),
  }))

  itemsWithParsedTime.sort((a, b) => a.timestamp - b.timestamp)

  return itemsWithParsedTime.map(({ item }) => item)
}

function groupItemsByActor(items: TimelineItem[]): MessageGroup[] {
  return items.reduce<MessageGroup[]>((groups, item) => {
    let actor: Actor
    if (item.type === TimelineItemType.AGENT_EVENT) {
      actor = item.data.eventType === EventType.USER_PROMPT ? Actor.USER : Actor.AGENT
    } else {
      actor = Actor.USER
    }

    const lastGroup = groups.at(-1)

    if (lastGroup?.actor === actor) {
      return [...groups.slice(0, -1), { ...lastGroup, items: [...lastGroup.items, item] }]
    }

    return [...groups, { actor, items: [item] }]
  }, [])
}
